<template>
  <base-dialog
    :title="getTitle()"
    :actionName="getActionName()"
    @action="getAction()"
    :actionDisabled="isValid()"
    :max-width="$vuetify.display.mdAndUp ? '30%' : '100%'"
    :showCancel="$t('common.action.cancel')"
    @close="showDialog = false"
    :isLoading="isLoading"
    :show="showDialog"
  >
    <template #content>
      <div v-if="dialogAction == 'delete'">
        <p align="center" class="text-body-1 mb-1 text-medium-emphasis font-weight-regular">
          {{ $t("page.customer.deleteCustomerMessage") }}
        </p>
      </div>
      <div v-else-if="dialogAction == 'note'">
        <v-textarea v-model="note" variant="outlined" density="comfortable" hide-details="auto">
          <template #label>
            <span class="app-field-label">note</span>
          </template>
        </v-textarea>
      </div>
      <div v-else class="ma-4">
        <p class="app-field-label">
          {{ $t("common.action.fullName") }}
        </p>
        <v-text-field v-model="name" @input="isValid" hide-details="auto" />
        <p class="app-field-label">
          {{ $t("common.action.email") }}
        </p>
        <v-text-field
          v-model="email"
          @input="isValid"
          type="email"
          :rules="[emailFormatRule]"
          hide-details="auto"
        />
        <p class="app-field-label">
          {{ $t("common.action.phone") }}
        </p>
        <v-text-field @input="isValid" v-model="phone" type="number" hide-details="auto" />

        <!-- Address Fields -->
        <v-divider class="my-4" />
        <p class="app-field-label">
          {{ $t("page.locations.country") }}
        </p>
        <v-select
          v-model="country"
          :items="allCountries"
          item-title="name"
          item-value="code"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          @update:modelValue="onCountryChange"
        />
        <p class="app-field-label">
          {{ $t("page.locations.addressLine1") }}
        </p>
        <v-text-field v-model="addressLine1" hide-details="auto" />
        <p class="app-field-label">
          {{ $t("page.locations.addressLine2") }}
        </p>
        <v-text-field v-model="addressLine2" hide-details="auto" />
        <p class="app-field-label">
          {{ $t("page.locations.city") }}
        </p>
        <v-text-field v-model="city" hide-details="auto" />
        <p class="app-field-label" v-if="showStateField">
          {{ getStateLabel() }}
        </p>
        <v-select
          v-if="showStateDropdown"
          v-model="state"
          :items="getStateItems()"
          item-title="name"
          item-value="code"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
        />
        <v-text-field v-else-if="showStateField" v-model="state" hide-details="auto" />
        <p class="app-field-label">
          {{ $t("page.locations.zipOrPostalCode") }}
        </p>
        <v-text-field
          v-model="postalCode"
          :placeholder="$t('page.locations.zipOrPostalCode')"
          hide-details="auto"
        />

        <!-- Tax Exempt Status -->
        <v-divider class="my-4" />
        <p class="app-field-label">
          {{ $t("page.customer.taxExempt") }}
        </p>
        <v-select
          v-model="taxExempt"
          :items="taxExemptOptions"
          item-title="label"
          item-value="value"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
        />
      </div>
    </template>
  </base-dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watchEffect } from "vue";
import BaseDialog from "@/shared/components/Common/BaseDialog.vue";
import { useValidation } from "@/shared/composables/validation";
import { useCustomersStore } from "@/customers/stores/customers";
import i18n from "@/app/plugins/i18n";
import { useAccountStore } from "@/account/stores/account";
import { useAuthStore } from "@/auth/stores/auth";
import { allCountries, usaState, canadianProvince } from "@/shared/constants/supportCountries";
import type { CreateCustomer, TaxExemptStatus } from "@/customers/types/customer";

const emit = defineEmits(["updated"]);
const showDialog = ref(false);
const isLoading = ref(false);
const selectedCustomer = ref();
const { name, email, phone, emailFormatRule } = useValidation();
const accountId = computed(() => useAccountStore()?.account?.id);
const accountName = computed(() => useAuthStore()?.authUser?.displayName);
const note = ref("");

// Address fields
const country = ref("");
const addressLine1 = ref("");
const addressLine2 = ref("");
const city = ref("");
const state = ref("");
const postalCode = ref("");

// Tax exempt field
const taxExempt = ref<TaxExemptStatus>("none");

const taxExemptOptions = computed(() => [
  { value: "none", label: i18n.global.t("page.customer.taxExemptOptions.none") },
  { value: "exempt", label: i18n.global.t("page.customer.taxExemptOptions.exempt") },
  { value: "reverse", label: i18n.global.t("page.customer.taxExemptOptions.reverse") },
]);

const showStateField = computed(() => {
  // Show state field for all countries, but dropdown only for US/CA
  return true;
});

const showStateDropdown = computed(() => {
  return country.value === "US" || country.value === "CA";
});

const getStateItems = () => {
  return country.value === "US" ? usaState : canadianProvince;
};

const getStateLabel = () => {
  return country.value === "US"
    ? i18n.global.t("page.locations.state")
    : country.value === "CA"
      ? i18n.global.t("page.locations.province")
      : i18n.global.t("page.locations.state");
};

const onCountryChange = () => {
  // Clear state when country changes if it's not US/CA
  if (country.value !== "US" && country.value !== "CA") {
    state.value = "";
  }
};

const reset = () => {
  name.value = "";
  email.value = "";
  phone.value = "";
  note.value = "";
  country.value = "";
  addressLine1.value = "";
  addressLine2.value = "";
  city.value = "";
  state.value = "";
  postalCode.value = "";
  taxExempt.value = "none";
};

const closeDialog = () => {
  reset();
  showDialog.value = false;
  emit("updated");
  isLoading.value = false;
};

const open = async (id?: string) => {
  reset();
  showDialog.value = true;
  isLoading.value = true;
  if (id) {
    selectedCustomer.value = id;
    const customer = await useCustomersStore().retrieveCustomer(id);
    if (customer) {
      name.value = customer.name ?? "";
      email.value = customer.email ?? "";
      phone.value = customer.phone ?? "";

      // Load address data if it exists
      if (customer.address) {
        country.value = customer.address.country || "";
        addressLine1.value = customer.address.line1 || "";
        addressLine2.value = customer.address.line2 || "";
        city.value = customer.address.city || "";
        state.value = customer.address.state || "";
        postalCode.value = customer.address.postal_code || "";
      }

      // Load tax exempt status
      taxExempt.value = customer.tax_exempt || "none";
    }
  }
  isLoading.value = false;
};

defineExpose({
  open,
});

const isValid = () => {
  return !name.value && (!email.value || !phone.value) && props.dialogAction !== "delete";
};

const getTitle = () => {
  switch (props.dialogAction) {
    case "create":
      return i18n.global.t("common.action.addCustomer");
    case "edit":
      return i18n.global.t("common.action.editCustomer");
    case "note":
      return i18n.global.t("page.customer.addNote");
    case "delete":
      return i18n.global.t("common.action.deleteCustomer");
    default:
      return "";
  }
};

const getActionName = () => {
  switch (props.dialogAction) {
    case "create":
      return i18n.global.t("common.action.create");
    case "edit":
      return i18n.global.t("common.action.save");
    case "note":
      return i18n.global.t("common.action.save");
    case "delete":
      return i18n.global.t("common.action.delete");
    default:
      return "";
  }
};

const getAction = () => {
  switch (props.dialogAction) {
    case "create":
      return createCustomer();
    case "edit":
      return editCustomer();
    case "note":
      return addNote();
    case "delete":
      return deleteCustomer();
    default:
      return "";
  }
};

// methods
const createCustomer = async () => {
  isLoading.value = true;

  const payload: CreateCustomer = {
    name: name.value,
    ...(email.value ? { email: email.value } : {}),
    ...(phone.value ? { phone: phone.value } : {}),
    ...(taxExempt.value && taxExempt.value !== "none" ? { tax_exempt: taxExempt.value } : {}),
  };

  // Add address if any address field is filled
  if (country.value || addressLine1.value || city.value || state.value || postalCode.value) {
    payload.address = {
      ...(country.value ? { country: country.value } : {}),
      ...(addressLine1.value ? { line1: addressLine1.value } : {}),
      ...(addressLine2.value ? { line2: addressLine2.value } : {}),
      ...(city.value ? { city: city.value } : {}),
      ...(state.value ? { state: state.value } : {}),
      ...(postalCode.value ? { postal_code: postalCode.value } : {}),
    };
  }

  const customer = await useCustomersStore().createCustomer(payload);

  if (customer) {
    closeDialog();
    emit("updated");
  } else {
    isLoading.value = false;
  }
};

const editCustomer = async () => {
  isLoading.value = true;
  const payload: CreateCustomer = {
    name: name.value,
    email: email.value,
    phone: phone.value,
    ...(taxExempt.value && taxExempt.value !== "none" ? { tax_exempt: taxExempt.value } : {}),
  };

  // Add address if any address field is filled
  if (country.value || addressLine1.value || city.value || state.value || postalCode.value) {
    payload.address = {
      ...(country.value ? { country: country.value } : {}),
      ...(addressLine1.value ? { line1: addressLine1.value } : {}),
      ...(addressLine2.value ? { line2: addressLine2.value } : {}),
      ...(city.value ? { city: city.value } : {}),
      ...(state.value ? { state: state.value } : {}),
      ...(postalCode.value ? { postal_code: postalCode.value } : {}),
    };
  }

  const customer = await useCustomersStore().updateCustomer(selectedCustomer.value, payload);
  if (customer) {
    closeDialog();
  } else {
    isLoading.value = false;
  }
};

const deleteCustomer = async () => {
  isLoading.value = true;
  const customer = await useCustomersStore().deleteCustomer(selectedCustomer.value);
  if (customer) {
    closeDialog();
  } else {
    isLoading.value = false;
  }
};

const addNote = async () => {
  isLoading.value = true;
  const payload = {
    createdBy: accountId.value,
    createdAt: new Date(),
    note: note.value,
    createdName: accountName.value,
  };
  await useCustomersStore().addCustomerNote(selectedCustomer.value, payload);

  closeDialog();
  isLoading.value = false;
};

const props = defineProps({
  dialogAction: {
    type: String,
    default: "create",
  },
});
</script>
